import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, IdCard, MessageCircle, Phone, ShieldCheck, Trees } from "lucide-react";
import { PageHero, usePageMeta } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, bookingHref, waLink } from "@/lib/site";

const WA_TOUR = waLink("Merhaba FX Motor Sporları, enduro tur (5.000 TL) için bilgi almak ve yer ayırtmak istiyorum.");

const PACKAGE = [
  "Tur öncesi kısa güvenlik bilgilendirmesi",
  "2 saat rehber eşliğinde enduro turu",
  "Enduro motosikleti kullanımı",
  "Kask ve gözlük",
  "Koruyucu ekipman (göğüs, dizlik, dirseklik)",
  "Bot ve eldiven",
  "Sürüş kıyafetleri",
  "İkram (su, çay vb.)",
  "Çekmeköy Ormanları'nda doğal parkur",
];

const FACTS = [
  { icon: Clock, k: "Tur süresi", v: "2 saat" },
  { icon: ShieldCheck, k: "Dahil", v: "Motor, ekipman, kıyafet" },
  { icon: Trees, k: "Parkur", v: "Çekmeköy Ormanları" },
  { icon: IdCard, k: "Şart", v: "A, A1 veya A2 ehliyet" },
];

const STEPS = [
  { t: "Yerini ayırt", d: "Tarihi seç, rezervasyon talebini gönder ya da WhatsApp'tan yaz." },
  { t: "Gel, giyin", d: "Motor ve ekipman hazır. Kısa güvenlik bilgilendirmesinin ardından parkura çıkılır." },
  { t: "Rehberle sür", d: "Profesyonel rehberler eşliğinde 2 saat ormanın içinde sür, dönüşte çayını iç." },
];

const PackageCard = () => (
  <div data-testid="tour-package-card" className="relative border border-white/15 bg-black p-8 md:p-10">
    <span className="absolute -top-3 left-8 -skew-x-12 bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white"><span className="block skew-x-12">Enduro Tur Paketi</span></span>
    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">Paket bedeli</p>
    <p data-testid="tour-price" className="mt-2 font-display text-7xl leading-none text-white md:text-8xl">5.000 TL</p>
    <p className="mt-3 text-sm text-neutral-400">Motor, ekipman ve kıyafetler dahil, 2 saatlik rehberli tur.</p>
    <ul className="mt-8 space-y-3 border-t border-white/10 pt-8">
      {PACKAGE.map((p) => <li key={p} className="flex gap-3 text-neutral-200"><Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" />{p}</li>)}
    </ul>
    <p className="mt-6 flex gap-3 border border-white/10 bg-white/[0.03] p-4 text-sm text-neutral-300"><IdCard className="h-5 w-5 shrink-0 text-brand" />Katılım için geçerli A, A1 veya A2 sınıfı motosiklet ehliyeti gereklidir.</p>
    <div className="mt-8 flex flex-col gap-3">
      <Link to={bookingHref("enduro-tur")} data-testid="tour-page-booking-btn" className="group flex items-center justify-center gap-3 bg-brand px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand">Tarih seç, yerini ayırt <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></Link>
      <a href={WA_TOUR} target="_blank" rel="noopener noreferrer" data-testid="tour-page-whatsapp-btn" className="flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:border-white"><MessageCircle className="h-5 w-5" /> WhatsApp'tan sor</a>
    </div>
  </div>
);

export const TourPage = () => {
  usePageMeta("Enduro Tur Çekmeköy Ömerli", "Çekmeköy Ormanları'nda rehberli 2 saatlik enduro turu: motor, ekipman ve kıyafetler dahil, 5.000 TL.");
  return (
  <>
    <PageHero eyebrow="Keşif & Macera" title="Enduro Tur" intro="Motorun yoksa da gel. Çekmeköy Ormanları'nın doğal parkurlarında, profesyonel rehberler eşliğinde 2 saatlik enduro turu." img="/reels/hero/h0.jpg" video="/reels/hero/h0.mp4" alt="FX parkurunda tepedeki enduro sürücüleri" crumbs={[{ label: "Enduro Tur" }]} />

    <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1.2fr_1fr]">
      <Reveal>
        <Eyebrow>FX Enduro Turu</Eyebrow>
        <h2 className="font-display text-5xl uppercase leading-none text-white md:text-7xl">Asfalt biter, macera başlar.</h2>
        <div className="mt-8 space-y-5 text-base leading-relaxed text-neutral-400 md:text-lg">
          <p>Şehrin hemen yanında, Ömerli'nin ormanlarında toprağa, çama ve özgürlüğe karışan bir sürüş. Enduro tur; enduroyu kendi motoru olmadan, güvenle denemek isteyen herkes için hazırlandı.</p>
          <p>Gelmen yeterli: enduro motosikleti, kask, gözlük, koruyucular, bot, eldiven ve sürüş kıyafetleri bizden. Kısa bir güvenlik bilgilendirmesinin ardından rehberlerimizle birlikte parkura çıkar, 2 saat boyunca doğanın içinde sürersin.</p>
          <p>Tur bitince soluklan; suyun, çayın hazır. Bir sonraki sefer için de yerini şimdiden ayırtabilirsin.</p>
        </div>
        <blockquote className="mt-10 border-l-2 border-brand pl-6 font-display text-3xl uppercase leading-tight text-white md:text-4xl">“Kaskını tak, gerisini parkura bırak.”</blockquote>
        <div className="mt-10 grid grid-cols-2 gap-3">
          <img src="/photos/tepe-zirve.jpg" alt="Tepenin zirvesinde enduro sürücüsü" loading="lazy" className="aspect-[4/5] w-full border border-white/10 object-cover" />
          <img src="/photos/toz-parkur.jpg" alt="Tozlu parkurda ilerleyen enduro sürücüsü" loading="lazy" className="mt-10 aspect-[4/5] w-full border border-white/10 object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1} className="order-first lg:order-none lg:sticky lg:top-28 lg:self-start">
        <PackageCard />
      </Reveal>
    </section>

    <section className="border-t border-white/10">
      <div className="mx-auto grid max-w-7xl grid-cols-2 md:grid-cols-4">
        {FACTS.map((f, i) => (
          <div key={f.k} data-testid={`tour-page-fact-${i}`} className="border-r border-white/10 px-5 py-10 last:border-r-0 md:px-8">
            <f.icon className="h-6 w-6 text-brand" strokeWidth={1.75} />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-neutral-500">{f.k}</p>
            <p className="mt-1 font-display text-2xl uppercase leading-tight text-white md:text-3xl">{f.v}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="border-t border-white/10 bg-surface">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
        <Reveal className="mb-14">
          <Eyebrow>Nasıl işler?</Eyebrow>
          <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">Üç adımda parkurdasın.</h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08} className="border border-white/10 bg-black p-8">
              <p className="font-display text-7xl leading-none text-brand">0{i + 1}</p>
              <h3 className="mt-6 font-display text-3xl uppercase text-white">{s.t}</h3>
              <p className="mt-3 text-neutral-400">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="tour-page-cta" className="bg-brand">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 px-5 py-16 md:flex-row md:items-center md:px-8 md:py-20">
        <div>
          <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">Tura yerini ayırt.</h2>
          <p className="mt-3 max-w-xl text-white/90">5.000 TL, motor, ekipman ve kıyafetler dahil. Bilgi için: {PHONE_DISPLAY}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to={bookingHref("enduro-tur")} data-testid="tour-cta-booking-btn" className="flex items-center justify-center gap-3 bg-black px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black">Rezervasyon yap <ArrowRight className="h-5 w-5" /></Link>
          <a href={PHONE_TEL} data-testid="tour-cta-call-btn" className="flex items-center justify-center gap-3 border border-white px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand"><Phone className="h-5 w-5" /> Ara</a>
        </div>
      </div>
    </section>
  </>
);
};
